import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  ShieldCheck, Upload, Play, Clock, Check, X, AlertTriangle, BadgeCheck,
  Lock, Video as VideoIcon, FileText,
} from "lucide-react";

// =============================================================================
// SIMPLIFIED VERIFICATION CENTER
//   • Single method: 15s+ selfie video showing face, full body, 360° rotation
//   • Holding paper with the word RELAXORA + today's date
//   • Strict privacy notice — video never appears anywhere public
// =============================================================================

const MIN_VIDEO_SECONDS = 15;

const INSTRUCTIONS = [
  { n: 1, txt: "Write \"RELAXORA\" and today's date on a piece of paper." },
  { n: 2, txt: "Record a video of at least 15 seconds." },
  { n: 3, txt: "Clearly show your face holding the paper." },
  { n: 4, txt: "Stand back so your full body is visible." },
  { n: 5, txt: "Slowly turn 360 degrees once in front of the camera." },
];

const STATUS_MAP = {
  not_verified: { color: "text-stone-400",   bg: "bg-stone-800/40 border-stone-700",       icon: AlertTriangle, label: "Not verified" },
  pending:      { color: "text-amber-300",   bg: "bg-amber-500/10 border-amber-500/30",    icon: Clock,         label: "Pending review" },
  verified:     { color: "text-emerald-300", bg: "bg-emerald-500/10 border-emerald-500/40", icon: BadgeCheck,    label: "Verified" },
  rejected:     { color: "text-rose-300",    bg: "bg-rose-500/10 border-rose-500/40",      icon: X,             label: "Rejected" },
};

export default function Verification() {
  const [myProfiles, setMyProfiles] = useState([]);
  const [profileId, setProfileId] = useState("");
  const [video, setVideo] = useState(null);
  const [videoMeta, setVideoMeta] = useState(null); // {durationSec}
  const [busy, setBusy] = useState(false);
  const [submissions, setSubmissions] = useState([]);

  const load = useCallback(async () => {
    try {
      const [p, v] = await Promise.all([
        api.get("/my/profiles"),
        api.get("/verification/mine"),
      ]);
      setMyProfiles(p.data.profiles);
      setSubmissions(v.data.verifications || []);
      if (!profileId && p.data.profiles[0]) setProfileId(p.data.profiles[0].id);
    } catch (e) { console.warn("[Verification] load error:", e); }
  }, [profileId]);
  useEffect(() => { load(); }, [load]);

  const currentProfile = myProfiles.find(p => p.id === profileId);
  const status = currentProfile?.verification_status
    || (currentProfile?.verification?.manual_status === "approved" ? "verified"
        : currentProfile?.verification?.manual_status === "pending" ? "pending"
        : currentProfile?.verification?.manual_status === "rejected" ? "rejected"
        : "not_verified");
  const submittedAt = currentProfile?.verification_submitted_at;
  const decidedAt = currentProfile?.verification_decided_at;
  const rejectionReason = currentProfile?.verification_rejection_reason;

  const isPending = status === "pending";
  const isVerified = status === "verified";

  // Inspect chosen file — read metadata for duration validation.
  const onPickVideo = (file) => {
    setVideo(file);
    setVideoMeta(null);
    if (!file) return;
    const url = URL.createObjectURL(file);
    const v = document.createElement("video");
    v.preload = "metadata";
    v.onloadedmetadata = () => {
      setVideoMeta({ durationSec: v.duration });
      URL.revokeObjectURL(url);
    };
    v.onerror = () => {
      setVideoMeta({ durationSec: 0, error: true });
      URL.revokeObjectURL(url);
    };
    v.src = url;
  };

  const submit = async () => {
    if (!video) { toast.error("Please attach your verification video"); return; }
    if (!profileId) { toast.error("Please select a profile to verify"); return; }
    if (videoMeta?.durationSec && videoMeta.durationSec < MIN_VIDEO_SECONDS) {
      toast.error(`Video must be at least ${MIN_VIDEO_SECONDS} seconds`); return;
    }
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", video);
      const upload = await api.post("/uploads/verification/video", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const submitFd = new FormData();
      submitFd.append("profile_id", profileId);
      submitFd.append("type", "profile_verified");
      submitFd.append("video_path", upload.data.path);
      await api.post("/verification/submit", submitFd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Submitted — awaiting moderator review");
      setVideo(null); setVideoMeta(null);
      load();
    } catch (e) {
      console.warn("[Verification] submit:", e);
      toast.error(e?.response?.data?.detail || "Submission failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 sm:py-12" data-testid="verification-page">
      <p className="text-overline">Trust</p>
      <h1 className="font-heading text-3xl sm:text-4xl text-white mt-2 font-light">Verification Center</h1>
      <p className="text-sm text-[#a3a3a3] mt-2 max-w-2xl">
        Earn a verified badge with a single short video. We use this only to confirm you are a real person — your video is never shown publicly.
      </p>

      {/* PRIVACY NOTICE */}
      <div className="mt-6 rounded-xl border border-amber-500/30 bg-amber-500/5 p-4 flex items-start gap-3" data-testid="verification-privacy-notice">
        <Lock className="w-5 h-5 text-amber-300 flex-none mt-0.5" />
        <div className="text-sm">
          <p className="text-amber-200 font-medium">Your video is private.</p>
          <p className="text-[13px] text-stone-300 mt-1 leading-relaxed">
            Your verification video is private and used only for moderation. It is never displayed publicly — not on profiles, galleries, search results or any media surfaces.
          </p>
        </div>
      </div>

      {/* PROFILE SELECTOR + STATUS */}
      <div className="mt-6 grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-3 items-start">
        {myProfiles.length > 0 ? (
          <select
            value={profileId}
            onChange={(e) => setProfileId(e.target.value)}
            className="w-full h-11 px-3 rounded-lg bg-stone-900/60 border border-stone-700 text-white text-sm"
            data-testid="verification-profile"
          >
            {myProfiles.map(p => (
              <option key={p.id} value={p.id}>{`${p.name} · ${p.city}`}</option>
            ))}
          </select>
        ) : (
          <div className="text-sm text-stone-400">Create a profile first to verify it.</div>
        )}
        <StatusPill status={status} />
      </div>

      {/* CURRENT-STATE BANNER */}
      {currentProfile && (
        <div className="mt-4 rounded-xl border border-stone-800 bg-stone-950/60 p-4 space-y-2 text-sm" data-testid="verification-status-card">
          <Row label="Status" v={<StatusPill status={status} inline />} />
          {submittedAt && <Row label="Submitted" v={new Date(submittedAt).toLocaleString()} testid="verif-submitted-at" />}
          {decidedAt && <Row label={isVerified ? "Verified on" : "Reviewed"} v={new Date(decidedAt).toLocaleString()} testid="verif-decided-at" />}
          {isVerified && (
            <div className="mt-2 flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-500/10 border border-emerald-500/40">
              <BadgeCheck className="w-4 h-4 text-emerald-300" />
              <span className="text-sm text-emerald-200 font-medium">Verified profile badge active</span>
            </div>
          )}
          {status === "rejected" && rejectionReason && (
            <div className="mt-2 p-3 rounded-lg bg-rose-500/10 border border-rose-500/40 text-rose-200 text-sm">
              <strong>Reason:</strong> {rejectionReason}
            </div>
          )}
        </div>
      )}

      {/* INSTRUCTIONS */}
      <div className="mt-6 rounded-xl border border-stone-800 bg-stone-950/60 overflow-hidden">
        <div className="px-4 sm:px-5 py-3 border-b border-stone-800 flex items-center gap-2">
          <VideoIcon className="w-4 h-4 text-amber-300" />
          <h3 className="text-sm font-medium text-stone-100">How to record your verification</h3>
        </div>
        <ol className="p-4 sm:p-5 space-y-2.5">
          {INSTRUCTIONS.map(i => (
            <li key={i.n} className="flex gap-3 text-sm text-stone-200">
              <span className="flex-none w-6 h-6 rounded-full bg-amber-500/15 border border-amber-500/40 text-amber-300 text-xs font-semibold flex items-center justify-center">{i.n}</span>
              <span>{i.txt}</span>
            </li>
          ))}
        </ol>
      </div>

      {/* UPLOAD / SUBMIT */}
      {!isPending && !isVerified && currentProfile && (
        <div className="mt-6 rounded-xl border border-stone-800 bg-stone-950/60 p-4 sm:p-5 space-y-4" data-testid="verification-form">
          <label className="block cursor-pointer">
            <span className="text-xs uppercase tracking-[0.18em] text-stone-400">Your video</span>
            <div className={`mt-2 rounded-xl border-2 border-dashed ${video ? "border-emerald-500/40" : "border-stone-700 hover:border-amber-500/60"} bg-stone-900/30 p-5 text-center transition`}>
              {video ? (
                <div className="space-y-2">
                  <div className="text-emerald-300 text-sm inline-flex items-center gap-1.5"><Check className="w-4 h-4" /> {video.name}</div>
                  <div className="text-xs text-stone-400">
                    {(video.size / (1024 * 1024)).toFixed(1)} MB
                    {videoMeta?.durationSec ? ` · ${Math.round(videoMeta.durationSec)}s` : ""}
                  </div>
                  {videoMeta?.durationSec && videoMeta.durationSec < MIN_VIDEO_SECONDS && (
                    <div className="text-xs text-rose-300">Too short — min {MIN_VIDEO_SECONDS} seconds</div>
                  )}
                  <button
                    type="button"
                    onClick={() => { setVideo(null); setVideoMeta(null); }}
                    className="text-xs text-stone-400 underline hover:text-stone-200"
                    data-testid="verification-clear-video"
                  >Choose a different file</button>
                </div>
              ) : (
                <div className="space-y-2">
                  <VideoIcon className="w-7 h-7 text-stone-500 mx-auto" />
                  <p className="text-sm text-stone-200">Tap to choose or record a video</p>
                  <p className="text-[11px] text-stone-500">MP4 / WebM / MOV · at least {MIN_VIDEO_SECONDS} seconds</p>
                </div>
              )}
              <input
                type="file"
                accept="video/*"
                capture="user"
                className="hidden"
                onChange={(e) => onPickVideo(e.target.files?.[0] || null)}
                data-testid="verification-video"
              />
            </div>
          </label>

          <button
            onClick={submit}
            disabled={busy || !video || (videoMeta?.durationSec && videoMeta.durationSec < MIN_VIDEO_SECONDS)}
            className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-amber-500 text-stone-950 rounded-lg px-6 py-3 text-sm font-semibold hover:bg-amber-400 disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="verification-submit"
          >
            <Upload className="w-4 h-4" /> {busy ? "Uploading…" : "Submit for review"}
          </button>
        </div>
      )}

      {/* IF PENDING — explain what happens next */}
      {isPending && (
        <div className="mt-6 rounded-xl border border-amber-500/30 bg-amber-500/5 p-4 flex items-start gap-3" data-testid="verification-pending-state">
          <Clock className="w-5 h-5 text-amber-300 flex-none mt-0.5" />
          <div className="text-sm">
            <p className="text-amber-200 font-medium">Pending moderator review</p>
            <p className="text-[13px] text-stone-300 mt-1">We&apos;re reviewing your video. You&apos;ll receive a result here within 24-48 hours.</p>
          </div>
        </div>
      )}

      {/* SUBMISSION HISTORY (sanitised — no media surfaces) */}
      {submissions.length > 0 && (
        <>
          <h2 className="font-heading text-xl text-white mt-10 mb-3">Submission history</h2>
          <div className="space-y-2">
            {submissions.map(s => {
              const conf = STATUS_MAP[s.status === "approved" ? "verified" : s.status === "pending" ? "pending" : s.status === "rejected" ? "rejected" : "not_verified"];
              const Icon = conf.icon;
              return (
                <div key={s.id} className="rounded-lg border border-stone-800 bg-stone-950/50 p-3 sm:p-4 flex items-center justify-between gap-3" data-testid={`my-verif-${s.id}`}>
                  <div className="min-w-0">
                    <p className="text-sm text-stone-200 flex items-center gap-1.5">
                      <FileText className="w-3.5 h-3.5 text-stone-500" /> Video verification
                    </p>
                    <p className="text-[11px] text-stone-500">{new Date(s.created_at).toLocaleString()}</p>
                  </div>
                  <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs border ${conf.bg}`}>
                    <Icon className={`w-3 h-3 ${conf.color}`} /> <span className={conf.color}>{conf.label}</span>
                  </span>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

function Row({ label, v, testid }) {
  return (
    <div className="flex items-center justify-between gap-3" data-testid={testid}>
      <span className="text-xs uppercase tracking-[0.18em] text-stone-400">{label}</span>
      <span className="text-sm text-stone-200">{v}</span>
    </div>
  );
}

function StatusPill({ status, inline = false }) {
  const conf = STATUS_MAP[status] || STATUS_MAP.not_verified;
  const Icon = conf.icon;
  return (
    <span className={`${inline ? "inline-flex" : "flex"} items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border ${conf.bg}`} data-testid={`verif-status-${status}`}>
      <Icon className={`w-3.5 h-3.5 ${conf.color}`} />
      <span className={conf.color}>{conf.label}</span>
    </span>
  );
}
